/**
 * Create Project page component.
 * Provisions a GitHub repository and stores project metadata.
 *
 * @module pages/CreateProjectPage
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FolderGit2,
  ChevronDown,
  ChevronRight,
  Terminal,
  Copy,
  Check,
  GitBranch,
  Lock,
  ExternalLink,
  Pencil,
  Loader2,
  CheckCircle2,
  Github,
  Play,
} from 'lucide-react';
import { useCreateProject, useCreateSandbox } from '@/hooks';
import { connectionService } from '@/services';
import { ApiException } from '@/services/httpClient';
import { useAuth } from '@/contexts/AuthContext';
import type { PresetSandboxType, ProjectRequest, ProjectResponse } from '@/types';

const REPO_NAME_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/;

function generateRepoName(): string {
  const id = Math.random().toString(36).slice(2, 8);
  return `project-${id}`;
}

function repoNameToDisplayName(repo: string): string {
  return repo
    .split('-')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ')
    .trim();
}

const ENV_PRESETS: { key: PresetSandboxType; label: string; description: string }[] = [
  { key: 'GitHubCopilot', label: 'Copilot', description: 'GitHub Copilot agent' },
  { key: 'Claude', label: 'Claude', description: 'Anthropic Claude agent' },
  { key: 'UbuntuSystemd', label: 'Ubuntu', description: 'Ubuntu with systemd' },
];

/* ------------------------------------------------------------------ */
/*  Small reusable bits                                                */
/* ------------------------------------------------------------------ */

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="absolute top-2 right-2 p-1 rounded text-gray-400 hover:text-gray-200 dark:hover:text-gray-200 transition-colors"
      title="Copy"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <div className="relative group">
      <pre className="bg-gray-100 dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-md px-3 py-2 text-xs font-mono text-gray-700 dark:text-gray-300 overflow-x-auto">
        {children}
      </pre>
      <CopyButton text={children} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Preview panel                                                      */
/* ------------------------------------------------------------------ */

function RepoPreview({
  ghUser,
  repoName,
  repoNameError,
  onRepoNameChange,
  onRepoNameValidate,
  linkedAccountLabel,
}: {
  ghUser: { login: string; avatarUrl: string } | null;
  repoName: string;
  repoNameError: string;
  onRepoNameChange: (v: string) => void;
  onRepoNameValidate: (v: string) => boolean;
  linkedAccountLabel?: string;
}) {
  const [editingRepoName, setEditingRepoName] = useState(false);
  const [localRepoName, setLocalRepoName] = useState(repoName);
  const inputRef = useRef<HTMLInputElement>(null);

  const owner = ghUser?.login ?? linkedAccountLabel ?? '???';
  const repo = repoName.trim() || '???';
  const full = `${owner}/${repo}`;
  const url = `https://github.com/${full}`;
  const hasUser = !!ghUser || !!linkedAccountLabel;
  const hasRepo = !!repoName.trim();

  const startEdit = () => {
    setLocalRepoName(repoName);
    setEditingRepoName(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const commitEdit = () => {
    const v = localRepoName.trim();
    if (v && onRepoNameValidate(v)) {
      onRepoNameChange(v);
    } else {
      setLocalRepoName(repoName);
    }
    setEditingRepoName(false);
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111] overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-[#2a2a2a] bg-gray-100 dark:bg-[#0d0d0d]">
        <div className="flex items-center gap-2">
          <FolderGit2 className="h-4 w-4 text-blue-500" />
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
            Repo Preview
          </span>
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5">What will be created on GitHub</p>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Owner */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-gray-500 uppercase tracking-wide">Owner</span>
          {ghUser ? (
            <span className="flex items-center gap-1.5 text-sm text-gray-900 dark:text-gray-100">
              <img src={ghUser.avatarUrl} alt={ghUser.login} className="h-5 w-5 rounded-full" />
              {ghUser.login}
            </span>
          ) : linkedAccountLabel ? (
            <span className="flex items-center gap-1.5 text-sm text-gray-900 dark:text-gray-100">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              {linkedAccountLabel}
            </span>
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-600 italic">
              Enter token first
            </span>
          )}
        </div>

        {/* Repository name — inline editable */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-gray-500 uppercase tracking-wide flex-shrink-0">
            Repository
          </span>
          {editingRepoName ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm text-gray-500 flex-shrink-0">{owner}/</span>
              <input
                ref={inputRef}
                type="text"
                value={localRepoName}
                onChange={(e) => setLocalRepoName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitEdit();
                  if (e.key === 'Escape') {
                    setLocalRepoName(repoName);
                    setEditingRepoName(false);
                  }
                }}
                onBlur={commitEdit}
                className="min-w-0 flex-1 bg-white dark:bg-[#0a0a0a] border border-blue-500 rounded px-1.5 py-0.5 text-sm font-mono text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={startEdit}
              className="group flex items-center gap-1.5 min-w-0 text-right"
            >
              <span
                className={`text-sm font-mono truncate ${hasRepo ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-600 italic'}`}
              >
                {hasRepo ? full : `${owner}/???`}
              </span>
              <Pencil className="h-3 w-3 text-gray-400 dark:text-gray-600 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors flex-shrink-0" />
            </button>
          )}
        </div>

        {/* URL */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-gray-500 uppercase tracking-wide flex-shrink-0">
            URL
          </span>
          {hasUser && hasRepo ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline font-mono truncate min-w-0"
            >
              <span className="truncate">{url}</span>
              <ExternalLink className="h-3 w-3 flex-shrink-0" />
            </a>
          ) : (
            <span className="text-sm text-gray-400 dark:text-gray-600 font-mono truncate">—</span>
          )}
        </div>

        {/* Visibility */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-gray-500 uppercase tracking-wide">Visibility</span>
          <span className="inline-flex items-center gap-1 text-sm text-gray-900 dark:text-gray-100">
            <Lock className="h-3 w-3" />
            Private
          </span>
        </div>

        {/* Branch */}
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-gray-500 uppercase tracking-wide">Branch</span>
          <span className="inline-flex items-center gap-1 text-sm text-gray-900 dark:text-gray-100">
            <GitBranch className="h-3 w-3" />
            main
          </span>
        </div>
      </div>

      {/* Repo-name validation error in preview context */}
      {editingRepoName && repoNameError && (
        <div className="px-4 pb-3">
          <p className="text-xs text-red-500">{repoNameError}</p>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

export function CreateProjectPage() {
  const navigate = useNavigate();
  const createMutation = useCreateProject();
  const createSandboxMutation = useCreateSandbox();
  const { user } = useAuth();

  const [repoName, setRepoName] = useState(() => generateRepoName());
  const [repoNameError, setRepoNameError] = useState('');
  const [gitHubToken, setGitHubToken] = useState('');
  // When true, the repo already exists on GitHub — next submit will adopt it
  const [repoExists, setRepoExists] = useState(false);
  const [showTokenHelp, setShowTokenHelp] = useState(false);
  const tokenDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Created project — when set, we show the env picker instead of the form
  const [createdProject, setCreatedProject] = useState<ProjectResponse | null>(null);
  // Tracks which environment is being launched
  const [launchingEnv, setLaunchingEnv] = useState<string | null>(null);

  // Copilot-linked GitHub account state
  const isEntraId = user?.authType === 'entra_id';
  const [copilotLinked, setCopilotLinked] = useState(false);
  // Error surfaced from option 1 failure (403, KV failure, etc.)
  const [option1Error, setOption1Error] = useState<string | null>(null);
  // Whether option 2 (manual token) is expanded
  const [showManualToken, setShowManualToken] = useState(!isEntraId);

  // Check copilot status on mount for Entra ID users
  useEffect(() => {
    if (!isEntraId) return;
    connectionService
      .getCopilotStatus()
      .then((status) => {
        if (status.hasReadyCopilotConnection && !status.needsReauthorization) {
          setCopilotLinked(true);
        }
      })
      .catch(() => {
        // Silently ignore — fall back to token input
      });
  }, [isEntraId]);

  // GitHub user lookup from token
  const [ghUser, setGhUser] = useState<{ login: string; avatarUrl: string } | null>(null);
  const [ghUserLoading, setGhUserLoading] = useState(false);
  const [ghUserError, setGhUserError] = useState('');
  const [lastValidatedToken, setLastValidatedToken] = useState('');

  const lookupGitHubUser = useCallback(
    async (token: string) => {
      const trimmed = token.trim();
      if (!trimmed || trimmed === lastValidatedToken) return;
      if (!/^(ghp_|gho_|github_pat_)/.test(trimmed)) {
        setGhUser(null);
        setGhUserError('Token must start with ghp_, gho_, or github_pat_');
        return;
      }

      setGhUserLoading(true);
      setGhUserError('');
      setGhUser(null);

      try {
        const res = await fetch('https://api.github.com/user', {
          headers: {
            Authorization: `Bearer ${trimmed}`,
            Accept: 'application/vnd.github+json',
          },
        });
        if (!res.ok) {
          setGhUserError(
            res.status === 401
              ? 'Token is invalid or expired.'
              : `GitHub API error (${res.status}).`
          );
          return;
        }
        const data = await res.json();
        setGhUser({ login: data.login, avatarUrl: data.avatar_url });
        setLastValidatedToken(trimmed);
      } catch {
        setGhUserError('Could not reach GitHub API.');
      } finally {
        setGhUserLoading(false);
      }
    },
    [lastValidatedToken]
  );

  const handleTokenBlur = () => {
    if (gitHubToken.trim()) {
      lookupGitHubUser(gitHubToken);
    }
  };

  const validateRepoName = (value: string) => {
    if (!value) {
      setRepoNameError('Repo name is required');
      return false;
    }
    if (!REPO_NAME_PATTERN.test(value)) {
      setRepoNameError(
        'Only lowercase letters, numbers, and hyphens. Must start and end with a letter or number.'
      );
      return false;
    }
    setRepoNameError('');
    return true;
  };

  const handleRepoNameChange = (value: string) => {
    setRepoName(value);
    setRepoExists(false);
    if (repoNameError) validateRepoName(value);
  };

  const renderTokenInput = () => (
    <div>
      <div className="relative">
        <input
          type="password"
          value={gitHubToken}
          onChange={(e) => {
            const v = e.target.value;
            setGitHubToken(v);
            setGhUser(null);
            setGhUserError('');
            setLastValidatedToken('');
            if (tokenDebounceRef.current) clearTimeout(tokenDebounceRef.current);
            if (v.trim()) {
              tokenDebounceRef.current = setTimeout(() => lookupGitHubUser(v), 600);
            }
          }}
          onBlur={handleTokenBlur}
          placeholder="ghp_... or gho_..."
          autoComplete="off"
          className={`w-full rounded-lg border px-3 py-2 pr-10 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 bg-white dark:bg-[#0a0a0a] focus:outline-none focus:ring-1 ${
            ghUserError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : ghUser
                ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
                : 'border-gray-300 dark:border-[#2a2a2a] focus:border-blue-500 focus:ring-blue-500'
          }`}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {ghUserLoading && <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />}
          {ghUser && !ghUserLoading && <Check className="h-4 w-4 text-green-500" />}
        </div>
      </div>
      {ghUser && (
        <div className="flex items-center gap-2 mt-1.5">
          <img src={ghUser.avatarUrl} alt={ghUser.login} className="h-4 w-4 rounded-full" />
          <span className="text-xs text-green-600 dark:text-green-400">
            Authenticated as <strong>{ghUser.login}</strong>
          </span>
        </div>
      )}
      {ghUserError && <p className="text-xs text-red-500 mt-1">{ghUserError}</p>}
      {!ghUser && !ghUserError && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          PAT (<code className="font-mono">ghp_</code>) or OAuth token (
          <code className="font-mono">gho_</code>) with <code className="font-mono">repo</code>{' '}
          scope.
        </p>
      )}
      <button
        type="button"
        onClick={() => setShowTokenHelp(!showTokenHelp)}
        className="inline-flex items-center gap-1 mt-2 text-xs text-blue-500 hover:text-blue-400 transition-colors"
      >
        {showTokenHelp ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <Terminal className="h-3 w-3" />
        How do I get a token?
      </button>
      {showTokenHelp && (
        <div className="mt-2 space-y-3 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111] p-3 sm:p-4">
          <div>
            <p className="text-xs font-medium text-gray-900 dark:text-gray-300 mb-1.5">
              GitHub CLI (recommended)
            </p>
            <div className="space-y-2">
              <CodeBlock>{'gh auth login'}</CodeBlock>
              <CodeBlock>{'gh auth token'}</CodeBlock>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">Copy the output and paste it above.</p>
          </div>
          <div className="border-t border-gray-200 dark:border-[#2a2a2a] pt-3">
            <p className="text-xs font-medium text-gray-900 dark:text-gray-300 mb-1.5">
              Fine-grained Personal Access Token
            </p>
            <ol className="text-xs text-gray-700 dark:text-gray-400 space-y-1.5 list-decimal list-inside">
              <li>
                Go to{' '}
                <a
                  href="https://github.com/settings/tokens?type=beta"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  github.com/settings/tokens
                </a>{' '}
                → <strong>Generate new token</strong>
              </li>
              <li>
                Set <strong>Repository access</strong> to <strong>All repositories</strong>
              </li>
              <li>
                Under <strong>Repository permissions</strong>, grant <strong>Administration</strong>{' '}
                and <strong>Contents</strong> → Read and write
              </li>
              <li>Generate and paste above</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateRepoName(repoName)) return;

    // If manual token is provided and validated, always use it (takes precedence over KV token)
    const displayName = repoNameToDisplayName(repoName.trim());
    const request: ProjectRequest =
      showManualToken && gitHubToken.trim() && ghUser
        ? {
            name: displayName,
            repoName: repoName.trim(),
            gitHubToken: gitHubToken.trim(),
            ...(repoExists && { useExisting: true }),
          }
        : {
            name: displayName,
            repoName: repoName.trim(),
            ...(repoExists && { useExisting: true }),
          };

    setOption1Error(null);

    createMutation.mutate(request, {
      onSuccess: (data) => {
        setRepoExists(false);
        setCreatedProject(data);
      },
      onError: (error) => {
        if (error instanceof ApiException && error.status === 409) {
          setRepoExists(true);
        } else if (error instanceof ApiException && error.status === 403) {
          const msg =
            error.detail ||
            error.message ||
            'GitHub returned 403 — token may lack repo permissions.';
          setOption1Error(msg);
          setShowManualToken(true);
          setShowTokenHelp(true);
        }
      },
    });
  };

  return (
    <div>
      {/* Back link */}
      <Link
        to="/projects"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 sm:mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Projects
      </Link>

      {/* ===== Success: env picker ===== */}
      {createdProject ? (
        <div className="max-w-lg mx-auto">
          <div className="border border-gray-200 dark:border-[#2a2a2a] rounded-lg bg-white dark:bg-[#161616] overflow-hidden">
            {/* Success header */}
            <div className="p-6 border-b border-gray-200 dark:border-[#2a2a2a] text-center">
              <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-3" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {createdProject.name}
              </h2>
              <a
                href={createdProject.repoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline mt-1"
              >
                {createdProject.repoFullName}
                <ExternalLink className="h-3 w-3" />
              </a>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                Choose a dev environment to start coding.
              </p>
            </div>

            {/* Environment buttons */}
            <div className="p-6 space-y-3">
              {ENV_PRESETS.map((preset) => {
                const isLaunching = launchingEnv === preset.key;
                return (
                  <button
                    key={preset.key}
                    disabled={!!launchingEnv}
                    onClick={async () => {
                      setLaunchingEnv(preset.key);
                      try {
                        const sandbox = await createSandboxMutation.mutateAsync({
                          presetSandboxType: preset.key,
                          projectId: createdProject.id,
                        });
                        navigate(`/sandboxes/${encodeURIComponent(sandbox.id)}`);
                      } catch {
                        // Error toast handled by hook
                      } finally {
                        setLaunchingEnv(null);
                      }
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1e1e1e] hover:border-gray-300 dark:hover:border-[#3a3a3a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
                  >
                    {isLaunching ? (
                      <Loader2 className="h-5 w-5 text-blue-500 animate-spin flex-shrink-0" />
                    ) : (
                      <Play className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {preset.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {preset.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Skip */}
            <div className="px-6 pb-6 text-center">
              <Link
                to="/projects"
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                Skip — I'll launch later from the Projects page
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] xl:grid-cols-[1fr_340px] 2xl:grid-cols-[1fr_400px] gap-4 lg:gap-6">
          {/* LEFT — Form card */}
          <div className="min-w-0">
            <div className="border border-gray-200 dark:border-[#2a2a2a] rounded-lg bg-white dark:bg-[#161616]">
              {/* Header */}
              <div className="flex items-center gap-3 p-4 sm:p-6 border-b border-gray-200 dark:border-[#2a2a2a]">
                <FolderGit2 className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <h1 className="text-base font-semibold text-gray-900 dark:text-white">
                    Create Project
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                    Provisions a private GitHub repo for your dev environment.
                  </p>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5">
                {/* Repo name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    GitHub Repo Name
                  </label>
                  <input
                    type="text"
                    value={repoName}
                    onChange={(e) => handleRepoNameChange(e.target.value)}
                    onBlur={() => validateRepoName(repoName)}
                    placeholder="my-project-repo"
                    required
                    className={`w-full rounded-lg border px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 bg-white dark:bg-[#0a0a0a] focus:outline-none focus:ring-1 ${
                      repoNameError
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 dark:border-[#2a2a2a] focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                  {repoNameError ? (
                    <p className="text-xs text-red-500 mt-1">{repoNameError}</p>
                  ) : (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Will be created as a private repo in the authenticated GitHub account.
                    </p>
                  )}
                </div>

                {/* GitHub Authentication */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    GitHub Authentication
                  </label>

                  {/* Option 1: OAuth / linked account — only for Entra ID */}
                  {isEntraId && (
                    <div
                      className={`rounded-lg border px-4 py-3 ${
                        option1Error
                          ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                          : copilotLinked
                            ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                            : 'border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111]'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {option1Error ? null : copilotLinked ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <Github className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            {option1Error ? (
                              <>
                                <p className="text-sm font-medium text-red-700 dark:text-red-300">
                                  GitHub authorization failed
                                </p>
                                <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 break-all">
                                  {option1Error}
                                </p>
                              </>
                            ) : copilotLinked ? (
                              <>
                                <p className="text-sm font-medium text-green-700 dark:text-green-300">
                                  GitHub account linked
                                </p>
                                <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                                  Repo will be created using your authorized connection.
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Authorize GitHub
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  Link your GitHub account to create repos automatically.
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                        {!copilotLinked && !option1Error && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const result = await connectionService.linkGitHub(
                                  window.location.href
                                );
                                window.location.href = result.consentLink;
                              } catch (err) {
                                const msg =
                                  err instanceof ApiException
                                    ? err.detail || err.message
                                    : 'Failed to start GitHub authorization.';
                                setOption1Error(msg);
                                setShowManualToken(true);
                                setShowTokenHelp(true);
                              }
                            }}
                            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 dark:bg-white hover:bg-gray-700 dark:hover:bg-gray-200 text-white dark:text-gray-900 text-xs font-medium rounded-lg transition-colors"
                          >
                            <Github className="h-3.5 w-3.5" />
                            Authorize
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Option 2: Manual token — always available, collapsed by default for Entra ID */}
                  {isEntraId ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowManualToken((v) => !v)}
                        className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-400 transition-colors"
                      >
                        {showManualToken ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                        {option1Error
                          ? 'Use a token instead (recommended)'
                          : copilotLinked
                            ? 'Use a different token instead'
                            : 'Use a token instead'}
                      </button>
                      {showManualToken && (
                        <div className="mt-2">
                          {option1Error && (
                            <div className="mb-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                              <p className="text-xs text-amber-700 dark:text-amber-300">
                                Paste a token from your <strong>local machine or CLI</strong> to
                                continue.
                              </p>
                            </div>
                          )}
                          {renderTokenInput()}
                        </div>
                      )}
                    </div>
                  ) : (
                    renderTokenInput()
                  )}
                </div>

                {/* ===== Inline preview (only on narrow screens where sidebar is hidden) ===== */}
                <div className="lg:hidden">
                  <RepoPreview
                    ghUser={ghUser}
                    repoName={repoName}
                    repoNameError={repoNameError}
                    onRepoNameChange={handleRepoNameChange}
                    onRepoNameValidate={validateRepoName}
                    linkedAccountLabel={
                      copilotLinked && !option1Error ? 'linked account' : undefined
                    }
                  />
                </div>

                {/* Repo exists notice — shown when 409 detected, button already morphed */}
                {repoExists && (
                  <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      A repository named <strong>{repoName.trim()}</strong> already exists in your
                      GitHub account. Click <strong>Use Existing Repo</strong> to link it.
                    </p>
                  </div>
                )}

                {/* Error from mutation — show for non-403/non-409 errors only */}
                {createMutation.isError &&
                  !(
                    createMutation.error instanceof ApiException &&
                    (createMutation.error.status === 403 || createMutation.error.status === 409)
                  ) && (
                    <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {(() => {
                          const err = createMutation.error;
                          const detail = err instanceof ApiException ? err.detail : undefined;
                          const message = err instanceof Error ? err.message : undefined;
                          return detail || message || 'Failed to create project.';
                        })()}
                      </p>
                    </div>
                  )}

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <Link
                    to="/projects"
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    disabled={
                      createMutation.isPending ||
                      !repoName.trim() ||
                      // Need either a linked account OR a validated manual token
                      (!copilotLinked && !(showManualToken && ghUser && !ghUserLoading))
                    }
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                    title={
                      !copilotLinked && !ghUser
                        ? 'Authorize GitHub or paste a token to continue'
                        : undefined
                    }
                  >
                    {createMutation.isPending
                      ? repoExists
                        ? 'Linking...'
                        : 'Creating...'
                      : ghUserLoading
                        ? 'Validating token...'
                        : repoExists
                          ? 'Use Existing Repo'
                          : 'Create Project'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* RIGHT — Sticky preview sidebar (shows from lg, grows with screen) */}
          <div className="hidden lg:block">
            <div className="sticky top-6 space-y-4">
              <RepoPreview
                ghUser={ghUser}
                repoName={repoName}
                repoNameError={repoNameError}
                onRepoNameChange={handleRepoNameChange}
                onRepoNameValidate={validateRepoName}
                linkedAccountLabel={copilotLinked && !option1Error ? 'linked account' : undefined}
              />

              {/* Mini help card */}
              <div className="rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#111] p-4">
                <p className="text-[11px] text-gray-500 uppercase tracking-wide font-semibold mb-2">
                  What happens next?
                </p>
                <ol className="text-xs text-gray-500 dark:text-gray-400 space-y-2 list-decimal list-inside">
                  <li>
                    A <strong className="text-gray-700 dark:text-gray-300">private repo</strong> is
                    created in your GitHub account
                  </li>
                  <li>
                    Launch a{' '}
                    <strong className="text-gray-700 dark:text-gray-300">dev environment</strong>{' '}
                    (Copilot, Claude, or Ubuntu) from the project card
                  </li>
                  <li>Your repo is auto-cloned on startup — start coding immediately</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
